import React, { useEffect, useMemo } from "react"
import { Button, ButtonToolbar, Col, Container, Form, Row, Spinner } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import OrderItem from "../../api/models/addOnTesting/ShoppingCart"
import { formatMoney } from "../../formatters/formatMoney"
import { useCurrentValueRef } from "../../hooks/useCurrentValueRef"

interface CartPanelProps {
  items: OrderItem[]
  onExpand: (item: OrderItem) => void
  onRemove: (item: OrderItem) => void
  onBack: () => void
  onNext: () => void
  processing: boolean
}

export function ShoppingCartStep({ items, onExpand, onRemove, onBack, onNext, processing }: CartPanelProps) {
  const { t } = useTranslation()

  const onBackRef = useCurrentValueRef(onBack)

  useEffect(() => {
    if (items.length === 0) {
      onBackRef.current()
    }
  }, [items, onBackRef])

  const sortedItems = useMemo(() => items.sort((itemA, itemB) => itemA.name.localeCompare(itemB.name)), [items])
  const total = useMemo(() => items.reduce((sum, item) => sum + item.itemPrice, 0), [items])
  const allEligible = useMemo(() => items.reduce((result, item) => result && item.eligible, true), [items])

  return <><Container className="container-fluid" >
    {sortedItems.map(item => {
      return <Row key={item.addOnTestingOrderItemId}>
        <Col xs={9} lg={6}>
          <Button variant="link" className="d-block p-0 text-decoration-none text-black text-start" onClick={() => onExpand(item)}>{item.name}</Button>
          {!item.eligible ? <Form.Control.Feedback type="invalid" className="d-block">{t("shoppingCart.ineligible")}</Form.Control.Feedback> : undefined}
          <Button variant="link" className="d-block p-0 mb-3 text-decoration-none link-primary" disabled={processing}
            onClick={() => onRemove(item)}>{t("shoppingCart.remove")}</Button>
        </Col>
        <Col xs={3} className="text-end">{formatMoney(item.itemPrice)}</Col>
      </Row>
    })}
    <h4>{t("shoppingCart.orderSummary")}</h4>
    <Row key="total" className="fw-bold mb-3">
      <Col xs={9} lg={6}>{t("shoppingCart.total")}</Col>
      <Col xs={3} className="text-end">{formatMoney(total)}</Col>
    </Row>
  </Container>
    <ButtonToolbar className="justify-content-between">
      <Button variant="secondary" onClick={onBack}>{t('shoppingCart.back')}</Button>
      <Button disabled={items.length === 0 || processing || !allEligible} onClick={onNext}>
        {processing && <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />}
        {t("shoppingCart.proceedToCheckout")}
      </Button>
    </ButtonToolbar>
  </>
}
